// web/src/pages/Conditions.jsx
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import HeaderAuth from "../components/common/HeaderAuth";
import { Link } from "react-router-dom";

export default function Conditions() {
  const origin =
    (typeof window !== "undefined" && window.location?.origin) ||
    "https://etatdulieu.com";

  const pageUrl = `${origin}/conditions`;
  const mail = "contact.etatdulieu@gmail.com";

  useEffect(() => {
    try {
      document.documentElement.style.colorScheme = "light";
    } catch {}
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Conditions générales d’utilisation & mentions légales — État du Lieu",
    url: pageUrl,
    isPartOf: { "@type": "WebSite", name: "État du Lieu", url: origin },
    dateModified: "2026-06-22",
  };

  return (
    <div className="legal-page">
      <Helmet prioritizeSeoTags>
        <link rel="canonical" href={pageUrl} />
        <title>Conditions générales d’utilisation & mentions légales — État du Lieu</title>
        <meta
          name="description"
          content="Conditions générales d’utilisation et mentions légales d’État du Lieu : accès au site, comptes, gratuité du service, publicité, responsabilité, propriété intellectuelle et droit applicable."
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Conditions générales d’utilisation & mentions légales — État du Lieu" />
        <meta
          property="og:description"
          content="Consultez les conditions d’utilisation du site État du Lieu, les mentions légales, les règles applicables aux comptes, à la publicité et à la responsabilité."
        />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={`${origin}/og/home.png`} />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <style>{`
        :root{
          --bg1:#3b82f6; --bg2:#1e40af;
          --surface:rgba(255,255,255,0.95);
          --ink:#0f172a; --muted:#64748b;
          --container-pad:16px;
          --auth-reserve:88px;
        }
        @supports (padding: max(0px)){
          :root{ --auth-reserve: max(76px, calc(env(safe-area-inset-top, 0px) + 64px)); }
        }
        html, body { margin:0; padding:0; color-scheme: light; }
        .legal-page{
          min-height:100vh;
          background: linear-gradient(135deg,var(--bg1) 0%, var(--bg2) 100%);
          color: var(--ink);
          font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen,Ubuntu,Cantarell,sans-serif;
          position: relative;
        }
        .wrap{
          max-width:1000px;
          margin:0 auto;
          padding: calc(28px + var(--auth-reserve)) var(--container-pad) 64px;
        }
        .glass-chip{
          background: var(--surface);
          backdrop-filter: blur(18px);
          border: 1px solid rgba(255,255,255,0.22);
          border-radius: 14px;
          padding: 8px 12px;
          box-shadow: 0 12px 28px rgba(0,0,0,0.12);
          display: inline-flex;
          align-items:center;
          gap:10px;
          color: var(--ink);
          text-decoration: none;
          font-weight: 600;
        }
        .back{ margin: 6px 0 16px; }

        .card{
          background: var(--surface);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 28px 24px 30px;
          border: 1px solid rgba(255,255,255,0.22);
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
        }
        h1{ font-size: clamp(2rem, 4vw, 2.6rem); margin: 0 0 8px; letter-spacing:-.01em; }
        h2{ font-size:1.25rem; margin:22px 0 8px; }
        h3{ font-size:1.05rem; margin:14px 0 6px; }
        p{ color:var(--muted); line-height:1.7; margin: .4rem 0 .9rem; }
        ul,ol{ color:var(--muted); padding-left:1.3rem; margin:.25rem 0 .9rem; line-height:1.7; }
        li{ margin:.15rem 0; }
        strong{ color:#111827; font-weight:600; }
        a{ color:#2563eb; text-decoration:none; }
        a:hover{ text-decoration:underline; }
        .subtitle{ font-size:.96rem; color:var(--muted); margin:0 0 12px; }
        .section-label{ font-size:.8rem; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:#9ca3af; margin-bottom:4px; }
        .toc{ margin:10px 0 18px; padding-left:18px; font-size:.95rem; }
        .toc li{ margin:4px 0; }
        @media (max-width: 640px){
          :root, html, body, .legal-page{ color-scheme: light; background:#ffffff; }
          .card{ background:#fff !important; }
        }
      `}</style>

      <div className="wrap">
        <HeaderAuth floating top={12} right={12} />
        <div className="back">
          <Link className="glass-chip back-chip" to="/">← Retour</Link>
        </div>

        <article className="card" aria-label="Conditions générales">
          <header>
            <div className="section-label">CGU &amp; Mentions légales</div>
            <h1>Conditions générales d’utilisation & mentions légales</h1>
            <p className="subtitle">
              Les présentes conditions encadrent l’accès au site, l’utilisation du service État du Lieu,
              la gestion des comptes utilisateurs, la présence de publicités sur le site et les règles
              applicables aux contenus mis à disposition.
            </p>
            <p>Dernière mise à jour : <strong>22 juin 2026</strong></p>
          </header>

          <nav aria-label="Sommaire">
            <ol className="toc">
              <li><a href="#mentions">1. Mentions légales</a></li>
              <li><a href="#objet">2. Objet & acceptation</a></li>
              <li><a href="#acces">3. Accès au site & compte utilisateur</a></li>
              <li><a href="#gratuit">4. Gratuité du service & publicité</a></li>
              <li><a href="#disponibilite">5. Disponibilité du site</a></li>
              <li><a href="#ip">6. Propriété intellectuelle</a></li>
              <li><a href="#usage">7. Usages interdits</a></li>
              <li><a href="#donnees">8. Données personnelles & consentement</a></li>
              <li><a href="#resp">9. Responsabilité</a></li>
              <li><a href="#signalement">10. Signalement & réclamations</a></li>
              <li><a href="#modif">11. Modification des conditions</a></li>
              <li><a href="#droit">12. Droit applicable & juridiction</a></li>
            </ol>
          </nav>

          <section id="mentions">
            <h2>1. Mentions légales</h2>
            <p>
              <strong>Éditeur du site</strong> : Antoine CHOUTEAU<br />
              <strong>Forme juridique</strong> : Entreprise individuelle (micro-entreprise)<br />
              <strong>Capital social</strong> : non applicable<br />
              <strong>Siège social</strong> : 14550 Blainville-sur-Orne – France<br />
              (Adresse complète communiquée sur demande légitime à <a href={`mailto:${mail}`}>{mail}</a>.)<br />
              <strong>SIRET</strong> : 99336134400019<br />
              <strong>Directeur de la publication</strong> : Antoine CHOUTEAU<br />
              <strong>Contact</strong> : <a href={`mailto:${mail}`}>{mail}</a>
            </p>
            <p>
              <strong>Hébergement</strong> : OVHcloud, 2 rue Kellermann – 59100 Roubaix – France.
            </p>
          </section>

          <section id="objet">
            <h2>2. Objet & acceptation</h2>
            <p>
              Les présentes conditions générales d’utilisation (ci-après les <strong>« CGU »</strong>)
              définissent les modalités d’accès et d’utilisation du site <strong>etatdulieu.com</strong>
              (ci-après le <strong>« Site »</strong>), édité par Antoine CHOUTEAU
              (ci-après l’<strong>« Éditeur »</strong>).
            </p>
            <p>
              Le Site permet notamment de consulter des informations et rapports relatifs à des adresses,
              à partir de données publiques, d’outils internes et de traitements techniques opérés
              par l’Éditeur.
            </p>
            <p>
              L’accès et l’utilisation du Site impliquent l’acceptation pleine et entière des présentes CGU.
              Si vous n’acceptez pas ces conditions, vous devez cesser d’utiliser le Site.
            </p>
          </section>

          <section id="acces">
            <h2>3. Accès au site & compte utilisateur</h2>
            <ul>
              <li>Certains contenus sont accessibles librement, d’autres fonctionnalités peuvent nécessiter un compte utilisateur.</li>
              <li>Vous vous engagez à fournir des informations exactes, complètes et à jour lors de la création de votre compte.</li>
              <li>Vous êtes responsable de la confidentialité de vos identifiants et de l’usage fait de votre compte.</li>
              <li>L’Éditeur peut suspendre ou restreindre l’accès à un compte en cas d’abus, de fraude, de risque pour la sécurité ou de non-respect des présentes CGU.</li>
            </ul>
          </section>

          <section id="gratuit">
            <h2>4. Gratuité du service & publicité</h2>
            <p>
              À la date des présentes, le service est proposé selon un modèle <strong>gratuit</strong>
              pour l’utilisateur final.
            </p>
            <p>
              Le financement du Site peut notamment reposer sur l’affichage de publicités fournies
              par des partenaires tiers, dont Google AdSense.
            </p>
            <p>
              L’affichage, la personnalisation éventuelle et la mesure de ces publicités peuvent dépendre
              des choix de consentement exprimés par l’utilisateur via l’interface prévue sur le Site.
            </p>
            <p>
              L’Éditeur ne garantit ni la disponibilité permanente des espaces publicitaires,
              ni le contenu exact des annonces affichées par les régies externes.
            </p>
          </section>

          <section id="disponibilite">
            <h2>5. Disponibilité du site</h2>
            <p>
              L’Éditeur s’efforce d’assurer un accès au Site dans des conditions raisonnables de disponibilité
              et de sécurité, sans garantir une disponibilité continue, permanente ni exempte d’erreurs.
            </p>
            <p>
              Des opérations de maintenance, mises à jour, évolutions techniques ou incidents indépendants
              de la volonté de l’Éditeur peuvent entraîner des interruptions temporaires du service.
            </p>
          </section>

          <section id="ip">
            <h2>6. Propriété intellectuelle</h2>
            <p>
              Le Site et l’ensemble de ses éléments, notamment les textes, marques, logos, graphismes,
              visuels, interfaces, scripts, bases de données, rapports, exports, fichiers et mises en page,
              sont protégés par les règles applicables en matière de propriété intellectuelle.
            </p>
            <p>
              Sauf autorisation écrite préalable de l’Éditeur ou cas expressément prévus par la loi,
              toute reproduction, extraction, réutilisation substantielle, diffusion, adaptation,
              commercialisation ou exploitation des contenus du Site est interdite.
            </p>
          </section>

          <section id="usage">
            <h2>7. Usages interdits</h2>
            <p>Il est notamment interdit :</p>
            <ul>
              <li>d’utiliser le Site à des fins illicites, frauduleuses ou contraires à l’ordre public ;</li>
              <li>de tenter de contourner les mesures de sécurité, de perturber le service ou d’accéder sans autorisation aux systèmes ;</li>
              <li>d’extraire de manière massive ou automatisée les données ou contenus du Site sans autorisation ;</li>
              <li>de porter atteinte aux droits de l’Éditeur, des utilisateurs ou de tiers.</li>
            </ul>
          </section>

          <section id="donnees">
            <h2>8. Données personnelles & consentement</h2>
            <p>
              La gestion des données personnelles, des traceurs, de la mesure d’audience, du consentement
              et de la publicité est décrite dans la{" "}
              <Link to="/rgpd">Politique de confidentialité (RGPD)</Link>.
            </p>
            <p>
              En utilisant le Site, vous reconnaissez avoir pris connaissance de cette politique.
            </p>
          </section>

          <section id="resp">
            <h2>9. Responsabilité</h2>
            <p>
              Les informations affichées sur le Site sont établies à partir de données, traitements
              et sources que l’Éditeur estime sérieuses, notamment des données publiques,
              mais elles peuvent comporter des erreurs, omissions, décalages de mise à jour
              ou limitations techniques.
            </p>
            <p>
              Le Site est fourni à titre informatif et d’aide à la consultation.
              Il ne constitue pas un conseil juridique, fiscal, financier, immobilier,
              commercial, médical ou professionnel.
            </p>
            <p>
              L’Éditeur ne pourra être tenu responsable des dommages indirects, pertes de chance,
              pertes d’exploitation, pertes de données ou préjudices résultant de l’utilisation
              ou de l’impossibilité d’utiliser le Site, sauf disposition légale impérative contraire.
            </p>
          </section>

          <section id="signalement">
            <h2>10. Signalement & réclamations</h2>
            <p>
              Pour toute question, signalement, difficulté technique ou réclamation, vous pouvez écrire à{" "}
              <a href={`mailto:${mail}`}>{mail}</a>.
            </p>
          </section>

          <section id="modif">
            <h2>11. Modification des conditions</h2>
            <p>
              L’Éditeur se réserve le droit de modifier à tout moment les présentes CGU
              afin de refléter l’évolution du Site, de ses fonctionnalités, de ses prestataires
              ou de la réglementation applicable.
            </p>
            <p>
              La version opposable est celle en vigueur à la date de votre utilisation du Site.
            </p>
          </section>

          <section id="droit">
            <h2>12. Droit applicable & juridiction</h2>
            <p>
              Les présentes CGU sont soumises au droit français.
            </p>
            <p>
              En cas de litige, et sous réserve des règles impératives applicables notamment
              en matière de protection des consommateurs, compétence est attribuée aux juridictions
              du ressort du siège social de l’Éditeur.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
}
